import {
  useCallback,
  type MutableRefObject,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import type { SpinButtonTextSelection } from '../../types.js';
import type { SpinButtonHandler } from '../types.js';

function handleArrowLeft(
  event: ReactKeyboardEvent,
  prevSpinButton: SpinButtonHandler | undefined,
  currentSpinButton: SpinButtonHandler | undefined,
  selection: SpinButtonTextSelection | undefined,
  onLeadingEdge?: () => void,
): void {
  // Cursor is at the start and there is a previous spin button — move focus to it
  if (prevSpinButton && selection?.start === 0) {
    prevSpinButton.element.current?.focus();
    event.preventDefault();
    // Cursor is at the start of the first spin button — notify the parent via callback
  } else if (!prevSpinButton && onLeadingEdge && selection?.start === 0) {
    onLeadingEdge();
    event.preventDefault();
    // No selection state available (e.g. spin button just received focus) — select all its content
  } else if (currentSpinButton && selection === undefined) {
    currentSpinButton.selectAll();
    event.preventDefault();
    // At the leading edge with no callback (e.g. the very first spin button of
    // a DateTimeRangePicker FROM input). Without preventDefault, Firefox would
    // shrink the selectAll'd range one character at a time on each press,
    // forcing the user to press ArrowLeft N times before anything changes.
  } else if (!prevSpinButton && selection?.start === 0) {
    event.preventDefault();
  }
}

function handleArrowRight(
  event: ReactKeyboardEvent,
  currentSpinButton: SpinButtonHandler | undefined,
  nextSpinButton: SpinButtonHandler | undefined,
  selection: SpinButtonTextSelection | undefined,
  length: number | undefined,
  onTrailingEdge?: () => void,
): void {
  // Cursor is at the end and there is a next spin button — move focus to it
  if (nextSpinButton && selection && selection.end === length) {
    nextSpinButton.element.current?.focus();
    event.preventDefault();
    // Cursor is at the end of the last spin button — notify the parent via callback
  } else if (
    !nextSpinButton &&
    onTrailingEdge &&
    selection &&
    selection.end === length
  ) {
    onTrailingEdge();
    event.preventDefault();
    // No selection state available (e.g. spin button just received focus) — select all its content
  } else if (currentSpinButton && selection === undefined) {
    currentSpinButton.selectAll();
    event.preventDefault();
    // At the trailing edge with no callback (e.g. the very last spin button of
    // a DateTimeRangePicker TO input). Block Firefox's shrink-by-one default
    // behaviour so the selectAll'd range stays put on each press.
  } else if (!nextSpinButton && selection?.end === length) {
    event.preventDefault();
  }
}

function handleDeletion(
  event: ReactKeyboardEvent,
  nextSpinButton: SpinButtonHandler | undefined,
  selection: SpinButtonTextSelection | undefined,
  length: number | undefined,
  onEdgeHandler?: () => void,
): void {
  // Only act when the spin button is empty (length === 0) and the cursor is collapsed
  // at position 0. If the spin button still has content, do nothing here — the browser
  // handles deleteContentForward on the non-collapsed selection produced by selectAll()
  // on focus, clearing the spin button in one keystroke. The second Delete press then
  // finds length === 0 and navigates to the next spin button.
  if (!length && selection?.start === 0 && selection?.end === 0) {
    if (nextSpinButton) {
      // Navigate forward: focus the next spin button. selectAll() fires on focus,
      // giving a non-collapsed selection so the first Delete press there clears
      // content rather than immediately navigating again.
      nextSpinButton.element.current?.focus();
      event.preventDefault();
    } else if (onEdgeHandler) {
      // Last spin button is empty — notify parent.
      event.preventDefault();
      requestAnimationFrame(() => onEdgeHandler());
    }
  }
}

/**
 * Checks if the pressed key is a trigger for moving to the next spin button and moves
 * focus accordingly.
 */
function handleTriggerNavigation(
  e: ReactKeyboardEvent,
  spinButtonRefs: MutableRefObject<SpinButtonHandler[]>,
  triggerRefs: MutableRefObject<string[]>,
  target: SpinButtonHandler,
): void {
  const lookupIndex = spinButtonRefs.current?.findIndex((h) => h === target);
  const nextTriggerIndex = triggerRefs.current.findIndex(
    (trigger, index) => trigger !== undefined && index > lookupIndex,
  );

  const afterTriggerSpinButton = spinButtonRefs.current?.find(
    (spinbutton, index) => spinbutton && index > nextTriggerIndex,
  );
  const trigger = triggerRefs.current[nextTriggerIndex];

  if (trigger !== undefined && afterTriggerSpinButton && e.key === trigger) {
    afterTriggerSpinButton.element.current?.focus();
    e.preventDefault();
  }
}

/**
 * If the user types a letter into a text spin button that has no next sibling spin button,
 * call the onTrailingEdge callback to allow moving focus to the next group.
 */
function handleTrailingTextAdvance(
  e: ReactKeyboardEvent,
  target: SpinButtonHandler,
  next: SpinButtonHandler | undefined,
  onTrailingEdge?: () => void,
): void {
  if (
    target.type !== 'text' ||
    next ||
    !/^[a-zA-Z]$/.test(e.key) ||
    e.metaKey ||
    e.ctrlKey
  ) {
    return;
  }

  const match = target.searchValue(e.key);
  if (match !== undefined) {
    onTrailingEdge?.();
  }
}

/**
 * This hook provides ootb support for arrow key and backspace navigation between spinbuttons within the group.
 * @param spinButtonRefs - A sparse array of spinbutton handlers
 * @param triggerRefs - A sparse array of trigger keys
 * @param onLeadingEdge - Optional callback fired when the user navigates backwards past the first spin button
 * @param onTrailingEdge - Optional callback fired when the user navigates forward past the last spin button
 */
export const useNavigateSpinButton = (
  spinButtonRefs: MutableRefObject<SpinButtonHandler[]>,
  triggerRefs: MutableRefObject<string[]>,
  onLeadingEdge?: () => void,
  onTrailingEdge?: () => void,
) => {
  const onKeyDownHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'Shift') {
        return;
      }

      const handlers = spinButtonRefs.current.filter(Boolean);
      const index = handlers?.findIndex(
        ({ element }) => element?.current === e?.target,
      );

      if (index < 0) {
        return;
      }

      const [prev, target, next] = [
        handlers[index - 1],
        handlers[index],
        handlers[index + 1],
      ];

      const length = target?.element?.current?.textContent?.length;
      const selection = target?.getSelection();

      if (!e.shiftKey) {
        switch (e.key) {
          case 'ArrowLeft':
            handleArrowLeft(e, prev, target, selection, onLeadingEdge);
            return;
          case 'ArrowRight':
            handleArrowRight(
              e,
              target,
              next,
              selection,
              length,
              onTrailingEdge,
            );
            return;
          case 'Backspace':
            handleDeletion(e, prev, selection, length, onLeadingEdge);
            return;
          case 'Delete':
            handleDeletion(e, next, selection, length, onTrailingEdge);
            return;
        }
      }

      handleTriggerNavigation(e, spinButtonRefs, triggerRefs, target);
      handleTrailingTextAdvance(e, target, next, onTrailingEdge);
    },
    [spinButtonRefs, triggerRefs, onLeadingEdge, onTrailingEdge],
  );

  return { onKeyDownHandler };
};
